import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { BRAND } from "../lib/brand";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `relative font-body text-sm tracking-wide transition-colors duration-300 ${
      isActive
        ? "text-[#c9a84c]"
        : "text-white/85 hover:text-[#c9a84c]"
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#1a2e5a]/95 backdrop-blur-xl border-b border-[#c9a84c]/20 shadow-lg"
          : "bg-[#1a2e5a]/70 backdrop-blur-md border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="logo-link"
        >
          <div className="w-10 h-10 rounded-full border-2 border-[#c9a84c] flex items-center justify-center bg-[#0a1530] group-hover:rotate-12 transition-transform duration-500">
            <span className="font-brand text-[#c9a84c] text-xl leading-none">
              A
            </span>
          </div>
          <div className="leading-none">
            <div className="font-brand text-white text-2xl md:text-[28px] tracking-wide">
              Archna's <span className="text-[#c9a84c]">Kitchen</span>
            </div>
            <div className="hidden md:block text-[10px] tracking-[0.28em] uppercase text-white/50 mt-1.5">
              Pure · Home-Cooked · Delhi
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-10">
          <NavLink to="/" end className={linkClass} data-testid="nav-home">
            Home
          </NavLink>
          <NavLink to="/menu" className={linkClass} data-testid="nav-menu">
            Menu
          </NavLink>
          <NavLink to="/contact" className={linkClass} data-testid="nav-contact">
            Contact
          </NavLink>
        </div>

        {/* Desktop CTA */}
        <a
          href={BRAND.whatsappChat}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:inline-flex items-center gap-2 bg-[#c9a84c] text-[#1a2e5a] font-black text-xs tracking-[0.08em] uppercase px-5 py-2.5 rounded-full hover:bg-[#d4b55a] hover:scale-105 hover:shadow-[0_0_24px_rgba(201,168,76,0.5)] transition-all duration-300"
          data-testid="navbar-whatsapp-cta"
        >
          <MessageCircle size={16} strokeWidth={2.5} />
          Order on WhatsApp
        </a>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-10 h-10 rounded-full border border-[#c9a84c]/40 text-[#c9a84c] flex items-center justify-center"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div
          className="md:hidden bg-[#0a1530] border-t border-[#c9a84c]/20 px-5 py-6 space-y-4"
          data-testid="mobile-menu"
        >
          <NavLink to="/" end className={linkClass} data-testid="mobile-nav-home">
            <div className="py-2 text-base">Home</div>
          </NavLink>
          <NavLink to="/menu" className={linkClass} data-testid="mobile-nav-menu">
            <div className="py-2 text-base">Menu</div>
          </NavLink>
          <NavLink to="/contact" className={linkClass} data-testid="mobile-nav-contact">
            <div className="py-2 text-base">Contact</div>
          </NavLink>
          <a
            href={BRAND.whatsappChat}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold w-full"
            data-testid="mobile-whatsapp-cta"
          >
            <MessageCircle size={16} strokeWidth={2.5} />
            Order on WhatsApp
          </a>
        </div>
      )}
    </header>
  );
}
